// src/components/KegReturns.tsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import '../App.css';
import { apiFetch } from '../utils/api';
import { API_BASE_URL } from '../config';
import { isValidKegCode, normalizeKegCode } from '../utils/kegCode';
import { SCAN_COOLDOWN_MS } from '../utils/kegScanFilter';
import KegScanOverlay, { KegScanAccepted } from './KegScanOverlay';
import { primeScanAudio } from '../utils/scanFeedback';

export interface KegReturnLine {
  id: number;
  returnId: number;
  kegCode: string;
  kegId?: number | null;
  packagingType?: string | null;
  productName?: string | null;
  locationName?: string | null;
  lastHistoryAction?: string | null;
  lastHistoryDate?: string | null;
  flag?: string | null;
  /** Taproom returns: whether approve will reduce that taproom's keg On Hand. */
  willReduceOnHand?: boolean | null;
  onHandReduceReason?: string | null;
  onHandReduceNote?: string | null;
}

export interface KegReturnTallies {
  packages: { packagingType: string; count: number }[];
  total: number;
  lineCount: number;
}

export interface KegReturn {
  id: number;
  status: 'draft' | 'approved' | string;
  /** 'distributor' (default) or 'taproom' (empties back from OBB / OBBA Taproom). */
  returnType?: 'distributor' | 'taproom' | string | null;
  customerId?: number | null;
  customerLabel?: string | null;
  notes?: string | null;
  createdAt?: string;
  updatedAt?: string;
  createdByEmail?: string | null;
  approvedAt?: string | null;
  approvedByEmail?: string | null;
  lines?: KegReturnLine[];
  groups?: { packagingType: string; lines: KegReturnLine[] }[];
  tallies?: KegReturnTallies;
  lineCount?: number;
  /** Taproom returns: how many lines will (or did) reduce taproom keg On Hand. */
  taproomOnHandReduceTotal?: number | null;
}

/** Taprooms that send empty kegs back to the outdoor racks. */
export const TAPROOMS = [
  { name: 'OBB Taproom', site: 'Madison' },
  { name: 'OBBA Taproom', site: 'Athens' },
];

export function isTaproomReturn(r?: Pick<KegReturn, 'returnType'> | null): boolean {
  return String(r?.returnType || '').toLowerCase() === 'taproom';
}

export interface Distributor {
  customerId: number;
  name: string;
}

/** Readable America/Chicago timestamp, e.g. "Oct 19, 2026, 4:39 PM CDT". */
export function formatCentral(value?: string | null): string {
  if (!value) return '';
  // SQLite datetime('now') has no zone: treat as UTC.
  const iso = /[zZ]|[+-]\d\d:?\d\d$/.test(value) ? value : `${value.replace(' ', 'T')}Z`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('en-US', {
    timeZone: 'America/Chicago',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

function useDistributors(): Distributor[] {
  const [rows, setRows] = useState<Distributor[]>([]);
  useEffect(() => {
    apiFetch(`${API_BASE_URL}/api/keg-returns/distributors`, { headers: { Accept: 'application/json' } })
      .then(async (r) => {
        if (r.ok) {
          const data = await r.json();
          setRows(Array.isArray(data) ? data : []);
        }
      })
      .catch(() => undefined);
  }, []);
  return rows;
}

async function deleteDraftRequest(id: number): Promise<void> {
  const res = await apiFetch(`${API_BASE_URL}/api/keg-returns/${id}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json' },
    allowHttpError: true,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Delete failed (${res.status})`);
  }
}

function confirmDeleteDraft(id: number): boolean {
  return window.confirm(
    `Delete draft return #${id}?\n\nScanned kegs on this draft are not moved. Kegs and inventory are not changed. This cannot be undone.`
  );
}

function flagLabel(flag?: string | null) {
  if (flag === 'unknown') return 'Unknown tag';
  if (flag === 'asset_destroyed') return 'Asset Destroyed';
  if (flag === 'duplicate') return 'Duplicate';
  if (flag === 'already_empty') return 'Already empty';
  if (flag === 'not_at_taproom') return 'Not at a taproom';
  if (flag === 'other_taproom') return 'Other taproom';
  if (flag === 'not_at_customer') return 'Not At Customer';
  return null;
}

/** Line-level warning copy when a scanned keg is not At Customer. */
function lineWarningText(line: KegReturnLine): string | null {
  if (line.flag === 'not_at_customer') {
    const where = line.locationName || line.lastHistoryAction || 'unknown location';
    const product = line.productName ? ` · ${line.productName}` : '';
    return `${line.kegCode}${product} is not At Customer (currently ${where})`;
  }
  return null;
}

/** Per-scan message from the server (Taproom Return validates every scan). */
export interface ScanNotice {
  level: 'ok' | 'warn' | 'error' | 'info' | string;
  message: string;
}

const KegReturnsList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const mode: 'distributor' | 'taproom' =
    searchParams.get('type') === 'taproom' ? 'taproom' : 'distributor';
  const [returns, setReturns] = useState<KegReturn[]>([]);
  const distributors = useDistributors();
  const [customerId, setCustomerId] = useState('');
  const [taproom, setTaproom] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'draft' | 'approved'>('all');

  const setMode = (next: 'distributor' | 'taproom') => {
    const params = new URLSearchParams(searchParams);
    if (next === 'taproom') params.set('type', 'taproom');
    else params.delete('type');
    setSearchParams(params, { replace: true });
    setError(null);
  };

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (filter !== 'all') params.set('status', filter);
    params.set('type', mode);
    const qs = `?${params.toString()}`;
    const res = await apiFetch(`${API_BASE_URL}/api/keg-returns${qs}`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`Failed to load returns: HTTP ${res.status}`);
    setReturns(await res.json());
  }, [filter, mode]);

  useEffect(() => {
    load().catch((err: Error) => setError(err.message));
  }, [load]);

  const startReturn = async () => {
    if (mode === 'taproom' && !taproom) {
      setError('Pick the taproom the empties came from');
      return;
    }
    if (mode === 'distributor' && !customerId) {
      setError('Pick the distributor this return is from');
      return;
    }
    try {
      setError(null);
      const res = await apiFetch(`${API_BASE_URL}/api/keg-returns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(
          mode === 'taproom'
            ? { returnType: 'taproom', taproom }
            : { customerId: Number(customerId) }
        ),
        allowHttpError: true,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      navigate(`/keg-returns/${body.id}`);
    } catch (err: any) {
      setError('Failed to start return: ' + err.message);
    }
  };

  const deleteDraft = async (id: number) => {
    if (!confirmDeleteDraft(id)) return;
    try {
      setError(null);
      await deleteDraftRequest(id);
      await load();
    } catch (err: any) {
      setError(err.message || 'Failed to delete draft');
    }
  };

  return (
    <div className="page-container keg-returns-page" data-testid="keg-returns-list">
      <h2 className="app-header" style={{ color: '#EEC930', fontSize: '24px' }}>
        {mode === 'taproom' ? 'Taproom Keg Returns' : 'Keg Returns'}
      </h2>
      <p style={{ color: '#555' }}>
        <Link to="/keg-tracking">← Keg Tracking</Link>
      </p>
      <div className="keg-return-mode" role="tablist" data-testid="keg-return-mode">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'distributor'}
          className={mode === 'distributor' ? 'is-on' : ''}
          data-testid="keg-return-mode-distributor"
          onClick={() => setMode('distributor')}
        >
          Distributor return
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'taproom'}
          className={mode === 'taproom' ? 'is-on' : ''}
          data-testid="keg-return-mode-taproom"
          onClick={() => setMode('taproom')}
        >
          Taproom return
        </button>
      </div>
      {error && <div className="error">{error}</div>}

      <div className="keg-returns-start" style={{ marginBottom: 20 }}>
        <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: '#555' }}>
          {mode === 'taproom' ? 'From taproom' : 'From distributor'}
        </label>
        {mode === 'taproom' && (
          <p className="keg-return-hint" data-testid="keg-return-taproom-hint">
            Scan empty kegs back from the taproom. They go to Outdoor Racks (Empty) with no product.
            Taproom keg On Hand drops only for kegs still in the last count (or arrived after it);
            no cooler inventory is added.
          </p>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          {mode === 'taproom' ? (
            <select
              value={taproom}
              onChange={(e) => setTaproom(e.target.value)}
              data-testid="keg-return-taproom"
              style={{ padding: 10, minWidth: 220 }}
            >
              <option value="">— Select taproom —</option>
              {TAPROOMS.map((t) => (
                <option key={t.name} value={t.name}>
                  {t.name} ({t.site})
                </option>
              ))}
            </select>
          ) : (
            <select
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              data-testid="keg-return-party"
              style={{ padding: 10, minWidth: 220 }}
            >
              <option value="" disabled>
                — Select distributor —
              </option>
              {distributors.map((d) => (
                <option key={d.customerId} value={String(d.customerId)}>
                  {d.name}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            data-testid="keg-return-start"
            onClick={startReturn}
            disabled={mode === 'distributor' && !customerId}
            style={{
              backgroundColor: '#28A745',
              color: '#fff',
              padding: '10px 20px',
              border: 'none',
              borderRadius: 4,
              cursor: mode === 'distributor' && !customerId ? 'not-allowed' : 'pointer',
              opacity: mode === 'distributor' && !customerId ? 0.6 : 1,
            }}
          >
            {mode === 'taproom' ? 'Start Taproom Return' : 'Start Return'}
          </button>
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as typeof filter)}
          data-testid="keg-returns-filter"
          style={{ padding: 8 }}
        >
          <option value="all">All returns</option>
          <option value="draft">Drafts</option>
          <option value="approved">Approved</option>
        </select>
      </div>

      <div className="keg-returns-list" data-testid="keg-returns-rows">
        {returns.length === 0 ? (
          <div className="alert-info">No returns yet.</div>
        ) : (
          returns.map((r) => (
            <div
              key={r.id}
              className="keg-return-list-item"
              data-testid={`keg-return-row-${r.id}`}
              onClick={() => navigate(`/keg-returns/${r.id}`)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter') navigate(`/keg-returns/${r.id}`);
              }}
            >
              <strong>
                Return #{r.id}{' '}
                <span className={`keg-return-status keg-return-status-${r.status}`}>{r.status}</span>
              </strong>
              <div>
                {isTaproomReturn(r) && <span className="keg-return-type-chip">Taproom</span>}
                {r.customerLabel ||
                  (isTaproomReturn(r) ? 'No taproom' : 'No distributor recorded')}{' '}
                · {r.tallies?.total ?? 0} kegs
                {r.tallies?.packages?.length
                  ? ` (${r.tallies.packages.map((p) => `${p.count}× ${p.packagingType}`).join(', ')})`
                  : ''}
              </div>
              <div style={{ fontSize: 13, color: '#666' }}>
                {r.status === 'approved'
                  ? `Approved ${formatCentral(r.approvedAt)} by ${r.approvedByEmail || '—'}`
                  : `Draft · updated ${formatCentral(r.updatedAt || r.createdAt)}`}
              </div>
              {r.status === 'draft' && (
                <button
                  type="button"
                  className="keg-return-delete"
                  data-testid={`keg-return-delete-${r.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    void deleteDraft(r.id);
                  }}
                  onKeyDown={(e) => e.stopPropagation()}
                  style={{
                    marginTop: 6,
                    backgroundColor: '#dc3545',
                    color: '#fff',
                    padding: '6px 12px',
                    border: 'none',
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                >
                  Delete draft
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const KegReturnDetail: React.FC = () => {
  const { returnId } = useParams<{ returnId: string }>();
  const navigate = useNavigate();
  const [ret, setRet] = useState<KegReturn | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [scanNotice, setScanNotice] = useState<ScanNotice | null>(null);
  const [scanCode, setScanCode] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const [busy, setBusy] = useState(false);
  const distributors = useDistributors();
  const inputRef = useRef<HTMLInputElement>(null);
  const lastScanRef = useRef<string>('');
  const retRef = useRef<KegReturn | null>(ret);
  retRef.current = ret;

  const load = useCallback(async () => {
    const res = await apiFetch(`${API_BASE_URL}/api/keg-returns/${returnId}`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`Failed to load return: HTTP ${res.status}`);
    setRet(await res.json());
  }, [returnId]);

  useEffect(() => {
    load().catch((err: Error) => setError(err.message));
  }, [load]);

  useEffect(() => {
    if (ret?.status === 'draft') {
      inputRef.current?.focus();
    }
  }, [ret?.status, ret?.lines?.length]);

  const saveCustomer = async (customerIdValue: string) => {
    if (!ret || ret.status !== 'draft') return;
    const res = await apiFetch(`${API_BASE_URL}/api/keg-returns/${ret.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(
        isTaproomReturn(ret)
          ? { taproom: customerIdValue }
          : { customerId: customerIdValue ? Number(customerIdValue) : null }
      ),
      allowHttpError: true,
    });
    const body = await res.json().catch(() => ({}));
    if (res.ok) setRet(body);
    else setError(body.error || `Save failed (${res.status})`);
  };

  const deleteDraft = async () => {
    if (!ret || ret.status !== 'draft') return;
    if (!confirmDeleteDraft(ret.id)) return;
    setBusy(true);
    try {
      await deleteDraftRequest(ret.id);
      navigate(isTaproomReturn(ret) ? '/keg-returns?type=taproom' : '/keg-returns');
    } catch (err: any) {
      setError(err.message || 'Failed to delete draft');
    } finally {
      setBusy(false);
    }
  };

  const addCode = async (raw: string) => {
    if (!ret || ret.status !== 'draft' || busy) return;
    const code = normalizeKegCode(raw);
    if (!isValidKegCode(code)) {
      setError('Valid keg tag needed (e.g., EK09503)');
      return;
    }
    // Debounce identical scans (same window as the shared camera confirmer)
    if (lastScanRef.current === code && Date.now() - (addCode as any)._ts < SCAN_COOLDOWN_MS) {
      return;
    }
    (addCode as any)._ts = Date.now();
    lastScanRef.current = code;

    setBusy(true);
    setError(null);
    setSuccess(null);
    setScanNotice(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/keg-returns/${ret.id}/lines`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ code }),
        allowHttpError: true,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(body.error || `Failed to add keg (${res.status})`);
        return;
      }
      setRet(body.return);
      setScanCode('');
      if (body.notice && (body.notice.level === 'warn' || body.notice.level === 'error')) {
        // Keep warnings on screen until the next scan so nothing fails silently.
        setScanNotice(body.notice);
      } else if (body.line?.flag) {
        setSuccess(`Added ${code} — ${flagLabel(body.line.flag)}`);
      } else {
        setSuccess(`Added ${code}`);
      }
      setTimeout(() => setSuccess(null), 1500);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  };

  /** Full-screen scanner: add one keg to the draft; throws (message shown in the scanner) when it can't. */
  const scanCodeFromCamera = async (code: string): Promise<KegScanAccepted> => {
    const current = retRef.current;
    if (!current || current.status !== 'draft') throw new Error('This return is not a draft');
    const res = await apiFetch(`${API_BASE_URL}/api/keg-returns/${current.id}/lines`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ code }),
      allowHttpError: true,
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.error || `Failed to add keg (${res.status})`);
    retRef.current = body.return;
    setRet(body.return);
    setError(null);
    if (body.notice && body.notice.level === 'error') {
      // Added, but flagged as a problem: keep it on the page too.
      setScanNotice(body.notice);
      return { level: 'warn', message: body.notice.message };
    }
    if (body.notice && body.notice.level === 'warn') {
      setScanNotice(body.notice);
      return { level: 'warn', message: body.notice.message };
    }
    if (body.line?.flag) return { level: 'warn', message: `Added ${code} — ${flagLabel(body.line.flag)}` };
    return { message: `Added ${code}` };
  };

  const removeLine = async (lineId: number) => {
    if (!ret || ret.status !== 'draft') return;
    setBusy(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/keg-returns/${ret.id}/lines/${lineId}`, {
        method: 'DELETE',
        headers: { Accept: 'application/json' },
        allowHttpError: true,
      });
      if (!res.ok) throw new Error(`Remove failed: HTTP ${res.status}`);
      setRet(await res.json());
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const approve = async () => {
    if (!ret || ret.status !== 'draft') return;
    if (!isTaproomReturn(ret) && !ret.customerId) {
      setError('Pick the distributor before approving');
      return;
    }
    const msg = isTaproomReturn(ret)
      ? `Approve taproom return #${ret.id} from ${ret.customerLabel}? Kegs will move to Outdoor Racks (Empty) with no product. Taproom keg On Hand drops only for kegs still in the last count (or arrived after it)${
          ret.taproomOnHandReduceTotal != null
            ? ` — ${ret.taproomOnHandReduceTotal} of ${(ret.lines || []).length} on this return`
            : ''
        }. No cooler inventory is added.`
      : `Approve return #${ret.id}? Kegs will move to Outdoor Racks (Empty).`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/keg-returns/${ret.id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({}),
        allowHttpError: true,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(body.error || `Approve failed (${res.status})`);
        return;
      }
      setRet(body);
      setSuccess(
        body.alreadyApproved
          ? 'This return was already approved, nothing changed'
          : 'Return approved'
      );
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!ret) {
    return (
      <div className="page-container">
        {error ? <div className="error">{error}</div> : <p>Loading return…</p>}
      </div>
    );
  }

  const readOnly = ret.status !== 'draft';
  const taproomReturn = isTaproomReturn(ret);
  const listPath = taproomReturn ? '/keg-returns?type=taproom' : '/keg-returns';
  // Older free-text value that doesn't match a distributor: show it, but make them pick one.
  const legacyLabel =
    !taproomReturn && !ret.customerId && ret.customerLabel ? String(ret.customerLabel) : null;
  const tallies = ret.tallies;

  return (
    <div
      className={`page-container keg-returns-page${readOnly ? ' keg-return-print' : ''}`}
      data-testid="keg-return-detail"
      data-status={ret.status}
    >
      <div className="keg-return-no-print">
        <p>
          <Link to={listPath}>← All {taproomReturn ? 'taproom ' : ''}returns</Link>
          {' · '}
          <Link to="/keg-tracking">Keg Tracking</Link>
        </p>
      </div>

      <h2 className="app-header" style={{ color: '#EEC930', fontSize: '24px' }}>
        {taproomReturn ? 'Taproom Return' : 'Return'} #{ret.id}{' '}
        <span className={`keg-return-status keg-return-status-${ret.status}`}>{ret.status}</span>
      </h2>

      {error && <div className="error">{error}</div>}
      {success && (
        <div className="error" style={{ color: '#28A745', backgroundColor: '#e6ffe6' }}>
          {success}
        </div>
      )}
      {scanNotice && (
        <div
          className={`tkc-notice tkc-notice--${scanNotice.level}`}
          role={scanNotice.level === 'error' ? 'alert' : 'status'}
          data-testid="keg-return-scan-notice"
          data-level={scanNotice.level}
        >
          {scanNotice.message}
        </div>
      )}

      <div className="keg-return-meta" style={{ marginBottom: 16, color: '#555' }}>
        <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>
          {taproomReturn ? 'From taproom' : 'From distributor'}
        </label>
        {readOnly ? (
          <div data-testid="keg-return-customer-ro">
            {ret.customerLabel || (taproomReturn ? '—' : 'No distributor recorded')}
          </div>
        ) : taproomReturn ? (
          <select
            value={ret.customerLabel || ''}
            onChange={(e) => {
              if (e.target.value) void saveCustomer(e.target.value);
            }}
            data-testid="keg-return-taproom-detail"
            style={{ padding: 10, width: '100%', maxWidth: 360 }}
          >
            {TAPROOMS.map((t) => (
              <option key={t.name} value={t.name}>
                {t.name} ({t.site})
              </option>
            ))}
          </select>
        ) : (
          <>
            <select
              value={ret.customerId ? String(ret.customerId) : legacyLabel ? '__legacy__' : ''}
              onChange={(e) => {
                if (e.target.value && e.target.value !== '__legacy__') void saveCustomer(e.target.value);
              }}
              data-testid="keg-return-customer"
              style={{ padding: 10, width: '100%', maxWidth: 360 }}
            >
              {legacyLabel ? (
                <option value="__legacy__" disabled>
                  {legacyLabel} (not a distributor — pick one)
                </option>
              ) : !ret.customerId ? (
                <option value="" disabled>
                  — Pick distributor —
                </option>
              ) : null}
              {distributors.map((d) => (
                <option key={d.customerId} value={String(d.customerId)}>
                  {d.name}
                </option>
              ))}
            </select>
            {legacyLabel && (
              <div data-testid="keg-return-legacy-hint" style={{ marginTop: 6, color: '#b35c00', fontSize: 14 }}>
                “{legacyLabel}” was typed in by hand. Pick the distributor before approving.
              </div>
            )}
          </>
        )}
        {readOnly && (
          <div style={{ marginTop: 8, fontSize: 14 }}>
            Approved {formatCentral(ret.approvedAt) || '—'} by {ret.approvedByEmail || '—'}
          </div>
        )}
      </div>

      <div className="keg-return-tallies" data-testid="keg-return-tallies">
        {(tallies?.packages || []).map((p) => (
          <span key={p.packagingType} className="keg-return-tally-chip">
            {p.count} × {p.packagingType}
          </span>
        ))}
        <span className="keg-return-tally-chip keg-return-tally-total" data-testid="keg-return-total">
          {tallies?.total ?? 0} total
        </span>
        {taproomReturn && ret.taproomOnHandReduceTotal != null && (
          <span
            className="keg-return-tally-chip"
            data-testid="keg-return-onhand-total"
            title={
              readOnly
                ? 'Kegs that reduced taproom keg On Hand on approve'
                : 'Kegs whose approve will reduce taproom keg On Hand'
            }
          >
            {ret.taproomOnHandReduceTotal}{' '}
            {readOnly ? 'reduced On Hand' : 'will reduce On Hand'}
          </span>
        )}
      </div>

      {!readOnly && (
        <div className="keg-return-scan keg-return-no-print" style={{ margin: '16px 0' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <input
              ref={inputRef}
              type="text"
              value={scanCode}
              onChange={(e) => setScanCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void addCode(scanCode);
                }
              }}
              placeholder="Scan or type keg tag"
              data-testid="keg-return-scan-input"
              disabled={busy}
              style={{ padding: 12, flex: '1 1 180px', fontSize: 16 }}
              autoComplete="off"
              autoCapitalize="characters"
            />
            <button
              type="button"
              data-testid="keg-return-add"
              disabled={busy}
              onClick={() => void addCode(scanCode)}
              style={{
                backgroundColor: '#2196F3',
                color: '#fff',
                padding: '12px 18px',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
              }}
            >
              Add
            </button>
            <button
              type="button"
              data-testid="keg-return-open-scanner"
              onClick={() => {
                primeScanAudio(); // iOS: sound needs this tap
                setShowScanner(true);
              }}
              style={{
                backgroundColor: '#555',
                color: '#fff',
                padding: '12px 18px',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
              }}
            >
              Camera
            </button>
          </div>
        </div>
      )}

      <div className="keg-return-groups" data-testid="keg-return-groups">
        {(ret.groups || []).map((group) => (
          <div key={group.packagingType} className="keg-return-group" data-testid={`keg-return-group-${group.packagingType}`}>
            <h3 style={{ color: '#555', fontSize: 16, margin: '16px 0 8px' }}>
              {group.packagingType} ({group.lines.length})
            </h3>
            {group.lines.map((line) => {
              const warn = lineWarningText(line);
              return (
              <div
                key={line.id}
                className={`keg-return-line${line.flag ? ` keg-return-line-flag-${line.flag}` : ''}`}
                data-testid={`keg-return-line-${line.kegCode}`}
              >
                <div className="keg-return-line-main">
                  <strong>{line.kegCode}</strong>
                  {flagLabel(line.flag) && (
                    <span className="keg-return-flag" data-testid={`keg-return-flag-${line.kegCode}`}>
                      {flagLabel(line.flag)}
                    </span>
                  )}
                </div>
                <div className="keg-return-line-meta">
                  {line.packagingType || '—'} · {line.productName || 'No product'} ·{' '}
                  {line.locationName || 'No location'}
                </div>
                {taproomReturn && line.willReduceOnHand != null && (
                  <div
                    className="keg-return-line-onhand"
                    data-testid={`keg-return-onhand-${line.kegCode}`}
                    data-reduce={line.willReduceOnHand ? 'yes' : 'no'}
                    style={{
                      color: line.willReduceOnHand ? '#1b5e20' : '#555',
                      fontSize: 14,
                      marginTop: 4,
                    }}
                  >
                    {readOnly
                      ? line.willReduceOnHand
                        ? 'Reduced taproom On Hand'
                        : 'Did not reduce taproom On Hand'
                      : line.willReduceOnHand
                        ? 'Will reduce taproom On Hand'
                        : 'Will not reduce taproom On Hand'}
                  </div>
                )}
                {warn && (
                  <div
                    className="keg-return-line-warn"
                    data-testid={`keg-return-warn-${line.kegCode}`}
                    style={{ color: '#b35c00', fontSize: 14, marginTop: 4 }}
                  >
                    {warn}
                  </div>
                )}
                <div className="keg-return-line-history">
                  Last:{' '}
                  {line.lastHistoryAction
                    ? `${line.lastHistoryAction}${line.lastHistoryDate ? ` (${line.lastHistoryDate})` : ''}`
                    : '—'}
                </div>
                {!readOnly && (
                  <button
                    type="button"
                    className="keg-return-no-print"
                    data-testid={`keg-return-remove-${line.kegCode}`}
                    onClick={() => void removeLine(line.id)}
                    disabled={busy}
                  >
                    Remove
                  </button>
                )}
              </div>
              );
            })}
          </div>
        ))}
        {(ret.lines || []).length === 0 && (
          <div className="alert-info">Scan or enter keg tags to build this return.</div>
        )}
      </div>

      <div className="keg-return-actions keg-return-no-print" style={{ marginTop: 20, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {!readOnly && (
          <button
            type="button"
            data-testid="keg-return-approve"
            onClick={() => void approve()}
            disabled={
              busy ||
              !(ret.lines && ret.lines.length) ||
              !!legacyLabel ||
              (!taproomReturn && !ret.customerId)
            }
            style={{
              backgroundColor: '#28A745',
              color: '#fff',
              padding: '12px 20px',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            Approve Return
          </button>
        )}
        {!readOnly && (
          <button
            type="button"
            data-testid="keg-return-delete"
            onClick={() => void deleteDraft()}
            disabled={busy}
            style={{
              backgroundColor: '#dc3545',
              color: '#fff',
              padding: '12px 20px',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            Delete draft
          </button>
        )}
        <button
          type="button"
          data-testid="keg-return-print"
          onClick={() => window.print()}
          style={{
            backgroundColor: '#555',
            color: '#fff',
            padding: '12px 20px',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
          }}
        >
          Print summary
        </button>
        <button
          type="button"
          onClick={() => navigate(listPath)}
          style={{
            backgroundColor: '#ccc',
            color: '#333',
            padding: '12px 20px',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
          }}
        >
          Back
        </button>
      </div>

      {showScanner && (
        <KegScanOverlay
          title={isTaproomReturn(ret) ? 'Scan taproom returns' : 'Scan keg returns'}
          subtitle={`Return #${ret.id}`}
          codes={(ret.lines || []).map((l) => l.kegCode)}
          describe={(c) => {
            const want = normalizeKegCode(c);
            const l = (ret.lines || []).find((x) => normalizeKegCode(x.kegCode) === want);
            return l ? `${l.packagingType || '—'} · ${l.productName || 'No product'}${l.flag ? ` · ${flagLabel(l.flag)}` : ''}` : null;
          }}
          onScan={scanCodeFromCamera}
          onRemove={async (c) => {
            const want = normalizeKegCode(c);
            const l = (retRef.current?.lines || []).find(
              (x) => normalizeKegCode(x.kegCode) === want
            );
            if (l) await removeLine(l.id);
          }}
          onDone={() => setShowScanner(false)}
        />
      )}
    </div>
  );
};

export { KegReturnsList, KegReturnDetail };
export default KegReturnsList;
